import { useState, useEffect, useRef } from 'react';
import { currency, currencyKR, decimalNumber } from '../helper/filter';
import {
  calculateBulletRepayment,
  calculateConstant,
  calculateConstantAmortization,
} from '../helper/interestCalculator';
import { useRecoilValue } from 'recoil';
import { beaconSelector } from '../recoil/selector';
import useBeacon from '../hooks/useBeacon';
import { PAGE_NM } from '../types/types';
import { useParams } from 'react-router-dom';
import { useTerms } from '../hooks/useTerms';

const useRateCalc = (preset?: {
  amount?: string;
  interestRate?: string;
  term?: string;
  maxTerm?: number;
}) => {
  const { media } = useParams();
  const [repayment, setRepayment] = useState(0);
  const [inputs, setInputs] = useState({
    loanAmount: preset?.amount || '',
    annualInterest: preset?.interestRate || '',
    loanTerm: preset?.term || '',
  });
  const [calculatedData, setCalculatedData] = useState<any>([]);
  const [result, setResult] = useState({
    totalLoanInterest: '',
    totalRepaymentAmount: '',
  });
  const [loanAmountWarningText, setLoanAmountWarningText] = useState('');
  const [annualInterestWarningText, setAnnualInterestWarningText] =
    useState('');
  const [loanTermWarningText, setLoanTermWarningText] = useState('');
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);
  const [isTableFolded, setIsTableFolded] = useState(true);
  const [focusedInput, setFocusedInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const inputRef3 = useRef<HTMLInputElement>(null);

  const RepaymentMap: any = [
    { label: '원리금균등', fn: calculateConstantAmortization },
    { label: '원금균등', fn: calculateConstant },
    { label: '만기일시', fn: calculateBulletRepayment },
  ];

  const calculate = (
    repayment: number,
    loanAmount: number,
    loanTerm: number,
    annualInterest: number,
  ) => {
    return RepaymentMap[repayment]?.fn(loanAmount, loanTerm, annualInterest);
  };

  const handleClickCalculate = (
    repayment: number,
    loanAmount: number,
    loanTerm: number,
    annualInterest: number,
  ) => {
    const result = calculate(repayment, loanAmount, loanTerm, annualInterest);
    setCalculatedData(result?.calculatedPaymentData);
    setResult({
      totalLoanInterest: `${currency(result?.interestTotal)} 원`,
      totalRepaymentAmount: `${currency(result?.totalPaymentAmount)} 원`,
    });

    //note blur focus
    setIsTextFieldFocused(false);
  };

  const handleClickTab = (event: any, data: { index: number }) => {
    setRepayment(data.index);
  };

  const handleInputChange = (
    value: string,
    name: 'loanAmount' | 'annualInterest' | 'loanTerm',
  ) => {
    if (name === 'loanAmount') {
      const commaRemovedLoanAmount = value.replace(/,/g, ''); //value 콤마 제거
      if (commaRemovedLoanAmount.length > 5) return; //자리수 제한
      setInputs({
        ...inputs,
        [name]: commaRemovedLoanAmount,
      });
    } else if (name === 'annualInterest') {
      const decimalValue = decimalNumber(value);
      const dotRemovedAnnualInterest = decimalValue.replace(/\./g, '');
      const dotsInValue = decimalValue.replace(/\d/g, '');
      const integerAndDecimalArray = decimalValue.split('.');

      if (integerAndDecimalArray[0] === '00') return;
      if (integerAndDecimalArray[0]?.length > 2) return;
      if (integerAndDecimalArray[1]?.length > 2) return;
      if (dotsInValue.length > 1) return;
      if (dotRemovedAnnualInterest.length > 4) return;

      setInputs({
        ...inputs,
        [name]: decimalValue,
      });
    } else if (name === 'loanTerm') {
      if (value.length > 3) return;
      setInputs({
        ...inputs,
        [name]: value,
      });
    }
  };

  const handleFocusTextField = () => {
    setIsTextFieldFocused(true);
  };

  const handleBlurTextField = () => {
    setIsTextFieldFocused(false);
  };

  const handleMouseDown = (e: any) => {
    e.preventDefault();
  };

  const handleIsTableFolded = (folded: boolean) => {
    setIsTableFolded(folded);
  };

  const handleFocusedInput = (inputName: string) => {
    setFocusedInput(inputName);
  };

  //note blur focus
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.blur();
    }

    if (inputRef2.current) {
      inputRef2.current.blur();
    }

    if (inputRef3.current) {
      inputRef3.current.blur();
    }

    if (!focusedInput) {
      if (inputRef.current) {
        inputRef.current.blur();
      }

      if (inputRef2.current) {
        inputRef2.current.blur();
      }

      if (inputRef3.current) {
        inputRef3.current.blur();
      }
    }
  }, [result, focusedInput]);

  //todo 리팩토링, warning text debounce 등 적용
  useEffect(() => {
    if (parseInt(inputs.loanAmount) === 0) {
      setLoanAmountWarningText('1만원 이상 입력해 주세요.');
    } else if (
      preset?.amount &&
      parseInt(inputs.loanAmount) > parseInt(preset.amount)
    ) {
      setLoanAmountWarningText(
        `${currencyKR(+preset.amount * 10000)}보다 적게 입력해 주세요.`,
      );
    } else {
      setLoanAmountWarningText('');
    }
  }, [inputs.loanAmount]);

  useEffect(() => {
    if (inputs?.annualInterest && Number(inputs.annualInterest) < 0.01) {
      setAnnualInterestWarningText('0.01% 이상 입력해 주세요.');
    } else {
      setAnnualInterestWarningText('');
    }
  }, [inputs.annualInterest]);

  useEffect(() => {
    if (parseInt(inputs.loanTerm) < 1) {
      setLoanTermWarningText('1개월 이상 입력해 주세요.');
    } else if (parseInt(inputs.loanTerm) > (preset?.maxTerm ?? 480)) {
      setLoanTermWarningText(
        `최대 ${preset?.maxTerm ?? 480}개월까지 입력 가능합니다.`,
      );
    } else {
      setLoanTermWarningText('');
    }
  }, [inputs.loanTerm]);

  useEffect(() => {
    if (calculatedData?.length) {
      handleClickCalculate(
        repayment,
        +inputs.loanAmount,
        +inputs.loanTerm,
        +inputs.annualInterest,
      );
    }
  }, [repayment]);

  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: window.location.pathname.includes('rateCalc')
      ? PAGE_NM.RATE_CALCULATOR
      : PAGE_NM.PRE_DETAIL,
  });
  useEffect(() => {
    if (window.location.pathname.includes('rateCalc')) {
      beacon.sendViewPage();
    }
  }, []);

  const sendBeaconClkBtnLoanApp = (btnNm: string) => {
    beacon.sendClkBtnLoanApp({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnAgree = (btnNm: string) => {
    beacon.sendClkBtnAgree({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnHelp = (btnNm: string) => {
    beacon.sendClkBtnHelp({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnCalc = (btnNm: string) => {
    const temp = {
      input_limit: inputs?.loanAmount ?? '',
      input_rate: inputs?.annualInterest ?? '',
      input_month: inputs?.loanTerm ?? '',
      select_method: RepaymentMap[repayment].label ?? '',
    };
    beacon.sendClkBtnCalc({
      properties: { btnNm, ...temp },
    });
  };
  const sendBeaconClkBtnCalcOutlink = (btnNm: string) => {
    beacon.sendClkBtnCalcOutlink({
      properties: { btnNm },
    });
  };

  const termProps = window.location.pathname.includes('/mtg')
    ? { EVENT_STATE: 'mtg', ref_page: PAGE_NM.MTG_HOME }
    : '';
  const terms = useTerms(termProps);

  return {
    repayment,
    isRepayment: RepaymentMap[repayment].label ?? null,
    inputs,
    setInputs,
    calculatedData,
    result,
    loanAmountWarningText,
    annualInterestWarningText,
    loanTermWarningText,
    focusedInput,
    isTextFieldFocused,
    isTableFolded,
    isInputBottomSheetOpen: !!focusedInput,
    calculate,
    handleClickCalculate,
    handleClickTab,
    handleInputChange,
    handleFocusTextField,
    handleFocusedInput,
    handleBlurTextField,
    handleMouseDown,
    handleIsTableFolded,
    inputRef,
    inputRef2,
    inputRef3,
    sendBeaconClkBtnAgree,
    sendBeaconClkBtnLoanApp,
    sendBeaconClkBtnHelp,
    sendBeaconClkBtnCalc,
    sendBeaconClkBtnCalcOutlink,
    hasAppbar: media !== 'kt',
    terms,
  };
};

export default useRateCalc;
